import React, { useState, useEffect, useMemo } from "react";
import { useLoading } from "../Loading";
import { collection, doc, getDoc, onSnapshot, query, Timestamp, where } from "firebase/firestore";
import { useAuth } from "../../contexts/authContext";
import db from "../../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import MobileStepper from '@mui/material/MobileStepper';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { CNav, CNavItem, CNavLink, CTabContent, CTabPane } from '@coreui/react'
import { Box } from "@mui/system";

function AppointmentList() {
    const { setLoading } = useLoading();
    const { currentUser } = useAuth();
    const [appointmentData, setAppointmentData] = useState();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [activeKey, setActiveKey] = useState(1);
    const [petNames, setPetName] = useState();
    const [filters, setFilters] = useState(
        {
            petType: "all",
            petName:"all",
        }
    );


    useEffect(() => {
        if (!currentUser) {
            navigate('/');
            return;
        }

        const appointmentRef = query(collection(db, 'Appointments'), where('shelterId', '==', currentUser.uid));

        const unsubscribeAppointmentList = onSnapshot(appointmentRef, (appointmentSnap) => {
            if (appointmentSnap.empty) {
                setAppointmentData([]);
                setLoading(false);
                return;
            }

            const appointmentList = appointmentSnap.docs.map((doc) => ({
                appointmentId: doc.id,
                ...doc.data(),
            }));

            const uniquePetName = new Set();
            const updatedAppointmentListPromises = appointmentList.map(async (app) => {
                let applicationData;
                try {
                    const applicationRef = doc(db, "Applications", app.appointmentId);
                    const applicationSnap = await getDoc(applicationRef);

                    if (applicationSnap.exists()) {
                        const { name, email, phoneNumber } = applicationSnap.data();
                        applicationData = { ...app, name, email, phoneNumber };
                    } else {
                        applicationData = { ...app, name: "", email: "", phoneNumber: "" };
                    }

                    const today = new Date();
                    const todayTimestamp = Timestamp.fromDate(
                        new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(),
                            today.getUTCHours(), today.getUTCMinutes(), today.getUTCSeconds(), today.getUTCMilliseconds()))
                    );

                    let condition;
                    if (app.appointmentTime > todayTimestamp) {
                        condition = "Upcoming";
                    } else {
                        condition = "History";
                    }

                    if (app.status === "Cancel") {
                        condition = "Cancel";
                    }

                    const petRef = doc(db, "Pets", app.petId);
                    const petSnap = await getDoc(petRef);

                    if (petSnap.exists()) {
                        const { petType, petName } = petSnap.data();
                        uniquePetName.add(petName);
                        return { ...applicationData, petType, petName, condition };
                    }

                    return { ...applicationData, petType: "", petName: "", condition };

                } catch (error) {
                    console.error(error);
                    return { ...applicationData, petType: "", petName: "", condition: "" };
                }
            });

            Promise.all(updatedAppointmentListPromises).then((updatedAppointmentList) => {
                setAppointmentData(updatedAppointmentList);
                const petNamesArray = Array.from(uniquePetName);
                setPetName(petNamesArray);
                setLoading(false);
            }).catch((error) => {
                console.error("Error updating appointment list:", error);
                setLoading(false);
            });

        }, (error) => {
            console.error("Error getting appointments:", error);
            setLoading(false);
        });

        return () => unsubscribeAppointmentList();
    }, [currentUser, navigate, setPetName]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((pre) => ({
            ...pre,
            [name]: value,
        }));
    };

    const filteredAppointment = useMemo(() => {
        if (!appointmentData) {
            return [];
        }

        let filtered =  appointmentData.filter((appointment) => {
            const appointmentName = appointment.email.toLowerCase();
            const searchTermLower = (search || "").toLowerCase();

            const typeMatch = filters.petType === "all" || appointment.petType === filters.petType;
            const nameMatch = filters.petName ==="all"|| appointment.petName === filters.petName;
            const searchMatch = appointmentName.includes(searchTermLower);
            const statusMatch =
                activeKey === 1
                    ? (appointment.condition === "Upcoming"&&appointment.status==="Pending")
                    : activeKey === 2
                        ? appointment.condition === "History"
                        : activeKey === 3
                            ? appointment.condition === "Cancel"
                            : false;
            return typeMatch && searchMatch && statusMatch&&nameMatch;
        });

        if (activeKey === 1 && filtered.length > 0) { // Upcoming
            filtered.sort((a, b) => a.appointmentTime.seconds - b.appointmentTime.seconds); // Nearest first
        } else if ((activeKey === 2||  activeKey === 3) && filtered.length > 0) { // History
            filtered.sort((a, b) => b.appointmentTime.seconds - a.appointmentTime.seconds); // Recently first
        }

        return filtered;
    }, [appointmentData, filters, search, activeKey]);



    const handleClearFilters = () => {
        setFilters({
            petType: "all",
            petName:"all",
        })
        setSearch("");
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);

    };

    const [activeStep, setActiveStep] = useState(0);
    const appointmentsPerPage = 10;
    const maxSteps = Math.ceil(filteredAppointment.length / appointmentsPerPage);
    const startIndex = activeStep * appointmentsPerPage;
    const currentAppointment = filteredAppointment.slice(startIndex, startIndex + appointmentsPerPage);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, maxSteps - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    return (
        <div className="applicationListContainer">
            <h2>Appointment List</h2>
            <CNav variant="tabs" role="tablist">
                <CNavItem>
                    <CNavLink active={activeKey === 1} onClick={() => setActiveKey(1)}>
                        Upcoming
                    </CNavLink>
                </CNavItem>
                <CNavItem>
                    <CNavLink active={activeKey === 2} onClick={() => setActiveKey(2)}>
                        History
                    </CNavLink>
                </CNavItem>
                <CNavItem>
                    <CNavLink active={activeKey === 3} onClick={() => setActiveKey(3)}>
                        Cancelled
                    </CNavLink>
                </CNavItem>
            </CNav>
            <div className="filterContainer">
                <input type="text" className="searchBox" placeholder="Search by applicant email" value={search} onChange={handleSearchChange} />
                <div>
                    <label htmlFor="petType">Pet Types</label> <br></br>
                    <select name="petType" id="petType" className="form-select form-select-sm"
                        onChange={handleFilterChange} value={filters.petType}
                    >
                        <option value="all">All</option>
                        <option value="Dog">Dog</option>
                        <option value="Cat">Cat</option>
                        <option value="Other">Other</option>

                    </select>
                </div>
                {petNames&&<div>
                    <label htmlFor="petName">Pet Name</label> <br></br>
                    <select name="petName" id="petName" className="form-select form-select-sm"
                        onChange={handleFilterChange} value={filters.petName}
                    >
                        <option value="all">All</option>
                        { petNames.map((name)=>
                        
                            <option key={name} value={name}>{name}</option>
                        )

                        }

                    </select>
                </div>}
                <div className="clearBtnContainer">
                    {(filters.petType !== "all" || search||filters.petName!=="all") && <button onClick={handleClearFilters} className="clearFilterBtn" >
                        Clear
                    </button>}
                </div>
            </div>

            <CTabContent>
                <CTabPane role="tabpanel" aria-labelledby="home-tab" visible={true}>
                    <table>
                        <thead >
                            <tr>
                                <th>Applicant Name</th>
                                <th>Contact</th>
                                <th>Pet Name</th>
                                <th>Pet Type</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentAppointment.map((appointment) => (
                                <tr key={appointment.appointmentId}>
                                    <td>{appointment.name}</td>
                                    <td>{appointment.phoneNumber}<br></br>{appointment.email}</td>
                                    <td>{appointment.petName}</td>
                                    <td>{appointment.petType}</td>
                                    <td>{appointment.status}</td>
                                    <td>{appointment.date}</td>
                                    <td>{appointment.time}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CTabPane>
            </CTabContent>


            <Box sx={{ height: 60, maxWidth: 400, width: '100%', p: 2, margin: "auto" }}>
                <MobileStepper
                    variant="text"
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1 || maxSteps === 0}>
                            Next <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            <KeyboardArrowLeft /> Back
                        </Button>
                    }
                />
            </Box>
           
        </div >
    );
}

export default AppointmentList;